import { useQuery } from "@tanstack/react-query";
import { useCurrentCompanyId } from "hooks/auth";
import { StaleTime } from "newui/store/tanstack-base";
import { getAllSubmissions } from "../Submission/submission-api";
import { RFX_DEFAULT_SORT } from "../config/rfx-sort-options";
import type { RfxSortValue } from "../config/rfx-sort-options";
import type { RfxTabConfig } from "../tabs/rfx-tab-config";
import type {
	RfxListPageMeta,
	RfxListQueryFilters,
	RfxListTabCounts,
} from "../hooks/useRfxList";
import type { RfxListSurface, RfxListSurfaceData } from "./rfx-list-surface";

/**
 * The bidder list's tabs are RESPONSE statuses — the state of this company's
 * submission — not members of `App\Enums\RfxListTab`. They are declared here,
 * separately, because `RfxListTab` mirrors that backend enum element-for-element
 * and is the wire contract for the three procurer scopes; adding values the API
 * will never send to it would quietly break that mirror.
 *
 * These five keys are the response keys of `GET /rfq/supplier-index` and must
 * stay in step with `RfxInviteViewAllService::getAllRfxWithDisplayData()` (and
 * with `getTabCounts()`, which badges the nav from the same vocabulary).
 */
export type BidderRfxTab =
	| "all"
	| "accepted"
	| "submitted"
	| "unsuccessful"
	| "awarded";

const BIDDER_RFX_TABS: RfxTabConfig<BidderRfxTab>[] = [
	{ tab: "all", label: "All" },
	{ tab: "accepted", label: "Accepted" },
	{ tab: "submitted", label: "Submitted" },
	{ tab: "unsuccessful", label: "Unsuccessful" },
	{ tab: "awarded", label: "Awarded" },
];

const BIDDER_TAB_KEYS = BIDDER_RFX_TABS.map(({ tab }) => tab as string);

export const BIDDER_RFX_DEFAULT_TAB: BidderRfxTab = "all";

/**
 * Matches the procurer shell's page size so both sides paginate identically.
 * The bidder list previously used 15 with infinite scroll; that scroll is being
 * replaced by the shell's pagination, so the odd size has nothing left to serve.
 */
const PER_PAGE = 12;

/** One row of `GET /rfq/supplier-index` — an RfxInvite, with the RFx nested. */
export interface BidderRfxListItem {
	rfq?: {
		id?: number;
		client?: string;
		logoPath?: string;
		name?: string;
		status?: string;
		submissionDeadline?: string;
		submissionTimezone?: string;
		supplierList?: { name?: string };
		unreadMessages?: number;
	};
	rfqId?: number;
	status?: string;
}

interface BidderTabPayload {
	data: BidderRfxListItem[];
	/** Note: only these two keys — NOT the procurer `RfxListPageMeta` shape. */
	meta: { last_page: number; total: number };
}

type BidderIndexResponse = Partial<Record<BidderRfxTab, BidderTabPayload>>;

export const bidderRfxListQueryKey = (
	companyId: number | null | undefined,
	page: number,
	search: string,
	sort: RfxSortValue
) => ["bidder-rfx-list", companyId ?? null, page, search, sort] as const;

/**
 * `GET /rfq/supplier-index` returns ALL FIVE tabs in one response — the
 * requested page of rows for each. That is wasteful for a single tab, but it is
 * the endpoint's existing contract and it makes the tab-strip counts free: every
 * tab's `meta.total` arrives whether or not the user has opened it, which is
 * exactly what `RfxListSurfaceData.counts` asks for. So the tab is NOT part of
 * the query key — switching tabs is a cache hit, not a refetch.
 */
const useBidderRfxList = (
	tab: BidderRfxTab,
	filters: RfxListQueryFilters
): RfxListSurfaceData<BidderRfxTab, BidderRfxListItem> => {
	const companyId = useCurrentCompanyId();
	const page = filters.page ?? 1;
	const search = filters.search ?? "";
	// Matches the procurer list's default so both surfaces open on the same order.
	const sort = filters.sort ?? RFX_DEFAULT_SORT.value;

	const query = useQuery<BidderIndexResponse>({
		queryKey: bidderRfxListQueryKey(companyId, page, search, sort),
		queryFn: async () => {
			// Double unwrap: axios `data`, then the API envelope's own `data`.
			const response = await getAllSubmissions(page, search, PER_PAGE, sort);

			return (response?.data?.data ?? {}) as BidderIndexResponse;
		},
		enabled: companyId !== null && companyId !== undefined,
		staleTime: StaleTime.Short,
		// Same reasoning as `useRfxList`: an RFx can be awarded or closed by the
		// procurer while this user sits on the tab, so remounting refetches while
		// the cached rows stay on screen.
		refetchOnMount: "always",
	});

	const payload = query.data?.[tab];

	const counts: RfxListTabCounts<BidderRfxTab> = {};
	BIDDER_RFX_TABS.forEach(({ tab: tabKey }) => {
		const total = query.data?.[tabKey]?.meta?.total;

		if (total !== undefined) {
			counts[tabKey] = total;
		}
	});

	// The endpoint's `meta` carries only `last_page`/`total`; the shell's
	// `Pagination` needs the full four. The two missing values are known here —
	// the page we asked for, and the size we asked for — so they are filled in
	// rather than made optional on the shared type.
	const meta: RfxListPageMeta | undefined = payload
		? {
				current_page: page,
				last_page: payload.meta?.last_page ?? 1,
				per_page: PER_PAGE,
				total: payload.meta?.total ?? 0,
			}
		: undefined;

	return {
		// No filter drawer on this surface yet — the endpoint returns no filter
		// options, so an empty set is the honest answer rather than a guess.
		filterOptions: {},
		counts,
		isError: query.isError,
		isLoading: query.isLoading,
		meta,
		rows: payload?.data ?? [],
	};
};

export const buildBidderRfxListPath = (
	companyId: number | string,
	tab: BidderRfxTab = BIDDER_RFX_DEFAULT_TAB
): string => `/account/${companyId}/rfx/tenders-quotes/${tab}`;

export const getBidderRfxListSurface = (): RfxListSurface<
	BidderRfxTab,
	BidderRfxListItem
> => ({
	defaultTab: BIDDER_RFX_DEFAULT_TAB,
	buildPath: buildBidderRfxListPath,
	isValidTab: (tab): tab is BidderRfxTab =>
		!!tab && BIDDER_TAB_KEYS.includes(tab),
	tabs: BIDDER_RFX_TABS,
	title: "Tenders & Quotes",
	useList: useBidderRfxList,
});
